import React, { useEffect, useState } from 'react';
import '../../App.css';
import { useSearchParams } from 'react-router-dom';
import {
	getProperDateTime,
	getToken,
	getUserPrimaryEmail,
	sessionStorageGetItem,
	sessionStorageSetItem,
	setLoginStatus,
	setUserData,
	setUserToken,
	setUserTokenExpiry,
	tokenIsValid,
} from '../Function';
import { LoadingScreenModal } from '../LoadingScreenModal';

function ManageBooking() {
	const SERVER_URL = process.env.REACT_APP_SERVER_URL;
	const [searchParams, setSearchParams] = useSearchParams();
	const token = searchParams.get('key');
	const token_expiry = searchParams.get('expiry');
	// Initialize synchronously from session storage so the first render already
	// reflects the real login state. Defaulting to false and setting it in a
	// post-mount useEffect caused a one-frame flash of the "not logged in" guard
	// (e.g. when returning here from bookingHistory?reschedule=true).
	const [login] = useState(
		() => JSON.parse(sessionStorageGetItem('login')) || false
	);
	const [loading, setLoading] = useState(true);
	const [userBookingHistoryData, setUserBookingHistoryData] = useState(null);
	const [upcomingBookingData, setUserUpcomingBookingData] = useState([]);
	const [completedBookingData, setCompletedBookingData] = useState([]);
	const [activeTab, setActiveTab] = useState('upcoming'); // 'upcoming' or 'past'

	const user_email = getUserPrimaryEmail();
	const checkLogin = JSON.parse(sessionStorageGetItem('login'));

	if (!token && !token_expiry && checkLogin == null) {
		window.location.href = '/';
	}

	useEffect(() => {
		setLoading(true);
		const getBookingHistoryData = async () => {
			try {
				const response = await fetch(
					`${SERVER_URL}/order/history/${user_email}`,
					{
						headers: {
							Authorization: getToken(),
						},
					}
				);
				if (!response.ok) {
					throw new Error(
						`This is an HTTP error: The status is ${response.status}`
					);
				}
				let actualData = await response.json();
				setUserBookingHistoryData(actualData);
			} catch (err) {
				setUserBookingHistoryData(null);
			} finally {
				setLoading(false);
			}
		};
		if (token) {
			const getUserProfileByToken = async () => {
				try {
					const response = await fetch(
						`${SERVER_URL}/user/profile/token/${token}/${token_expiry}`,
						{
							headers: {
								Authorization: getToken(),
							},
						}
					);
					if (!response.ok) {
						throw new Error(
							`This is an HTTP error: The status is ${response.status}`
						);
					}
					let actualData = await response.json();
					setLoginStatus(true);
					setUserToken(token);
					setUserTokenExpiry(token_expiry);
					setUserData(actualData);
					sessionStorageSetItem('userData', JSON.stringify(actualData));
					sessionStorageSetItem('login', JSON.stringify(true));
					sessionStorageSetItem(
						'primaryEmail',
						JSON.stringify(actualData.primaryemail)
					);
					if (actualData && !user_email) {
						window.location.reload();
					}
				} catch (err) {
					console.log(err);
				} finally {
					setLoading(false);
				}
			};
			tokenIsValid();
			getUserProfileByToken();
		}
		tokenIsValid();
		getBookingHistoryData();
	}, [getToken()]);

	useEffect(() => {
		if (userBookingHistoryData != null && userBookingHistoryData.length !== 0) {
			var now = new Date();
			let upcoming = [];
			let completed = [];

			userBookingHistoryData.forEach((element) => {
				if (element.ordernumber !== '') {
					var orderDate = getProperDateTime(
						element.departdate,
						element.departtime
					);

					if (
						element.returndate !== '' &&
						element.returntime !== '' &&
						element.returndate !== null &&
						element.returntime !== null
					) {
						orderDate = getProperDateTime(
							element.returndate,
							element.returntime
						);
					}

					if (now < orderDate) {
						upcoming.push(element);
					} else {
						completed.push(element);
					}
				}
			});

			upcoming.sort((a, b) => new Date(a.departdate) - new Date(b.departdate));
			completed.sort((a, b) => new Date(b.departdate) - new Date(a.departdate)); // Past stays: most recent first

			setUserUpcomingBookingData(upcoming);
			setCompletedBookingData(completed);
		}
	}, [userBookingHistoryData]);

	const linkToBookingHistory = (id, addon, oldcartguid, departbookingcode, returnbookingcode) => {
		sessionStorageSetItem('history_guid', JSON.stringify(id));
		sessionStorageSetItem('addon', JSON.stringify(addon));
		sessionStorageSetItem('oldcartguid', JSON.stringify(oldcartguid));
		sessionStorageSetItem('depart_booking_code', JSON.stringify(departbookingcode));
		sessionStorageSetItem('return_booking_code', JSON.stringify(returnbookingcode));

		const rescheduleData = {
			history_guid: id,
			addon: addon,
			oldcartguid: oldcartguid,
			depart_booking_code: departbookingcode,
			return_booking_code: returnbookingcode,
		};
		sessionStorageSetItem('reschedule_data', JSON.stringify(rescheduleData));
	};

	const getDepartAndDestination = (details, type = 'Depart') => {
		if (!details) return '';
		const parts = details.split(' - ');
		return type === 'Depart' ? parts[0] : parts[1] || '';
	};

	const formatDate = (dateStr) => {
		if (!dateStr) return '';
		const date = new Date(dateStr);
		return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
	};

	const formatTime = (timeStr) => {
		if (!timeStr) return '';
		const [hours, minutes] = timeStr.split(':');
		const h = parseInt(hours);
		const ampm = h >= 12 ? 'PM' : 'AM';
		const h12 = h % 12 || 12;
		return `${h12}:${minutes} ${ampm}`;
	};

	if (!login) {
		return (
			<div className="flex min-h-screen items-center justify-center bg-slate-50">
				<div className="text-center">
					<h1 className="text-2xl font-bold text-slate-800">You are not logged in!</h1>
					<p className="mt-2 text-slate-600">Please log in to view your bookings.</p>
					<button
						onClick={() => window.location.href = '/'}
						className="mt-6 rounded-lg bg-primary px-6 py-2 font-semibold text-white shadow-md hover:bg-primary/90 transition-colors"
					>
						Go to Login
					</button>
				</div>
			</div>
		);
	}

	return (
		<div className="tailwind-scope">
			<div className="min-h-screen bg-[#f8fafc] dark:bg-slate-950 text-slate-900 dark:text-slate-100 font-sans selection:bg-primary/10">
				{loading && <LoadingScreenModal />}

				<main className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8">
					{/* Header Section */}
					{/* <div className="mb-10 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
						<div>
							<h1 className="text-3xl font-extrabold tracking-tight text-left text-slate-900 dark:text-white sm:text-4xl">My Bookings</h1>
							<p className="mt-2 text-lg text-slate-500 dark:text-slate-400">Manage and view all your travel journeys in one place.</p>
						</div>
					</div> */}

					{/* Tabs Section */}
					<div className="mb-8" style={{ borderBottom: '1px solid #ddd' }}>
						<nav className="flex gap-8" aria-label="Tabs">
							<a
								href="#"
								onClick={(e) => { e.preventDefault(); setActiveTab('upcoming'); }}
								className={`flex items-center gap-2 border-b-2 pb-4 text-sm font-bold no-underline transition-all duration-300 ${activeTab === 'upcoming'
									? 'border-primary text-primary'
									: 'border-transparent text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'
									}`}
								style={{
									borderBottom: activeTab === 'upcoming' ? 'solid ' : 'none'
								}}
							>
								<span className={`material-symbols-outlined text-lg ${activeTab === 'upcoming' ? 'FILL' : ''}`}>calendar_today</span>
								Upcoming Bookings
							</a>
							<a
								href="#"
								onClick={(e) => { e.preventDefault(); setActiveTab('past'); }}
								className={`flex items-center gap-2 border-b-2 pb-4 text-sm font-bold no-underline transition-all duration-300 ${activeTab === 'past'
									? 'border-primary text-primary'
									: 'border-transparent text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'
									}`}
								style={{
									borderBottom: activeTab === 'past' ? 'solid ' : 'none'
								}}
							>
								<span className={`material-symbols-outlined text-lg ${activeTab === 'past' ? 'FILL' : ''}`}>history</span>
								Past Bookings
							</a>
						</nav>
					</div>

					{activeTab === 'upcoming' ? (
						<div className="space-y-8">
							<div className="flex items-center justify-between">
								<div className="flex items-center gap-3">
									<div className="h-8 w-1 bg-primary rounded-full"></div>
									<h2 className="text-xl font-bold text-slate-800 dark:text-slate-200">Confirmed Journeys</h2>
								</div>
								<button
									onClick={() => window.location.href = '/'}
									className="group flex items-center gap-2 rounded-full  px-4 py-2 text-sm font-bold text-primary border-none transition-all hover:bg-primary/10"
								>
									<span className="material-symbols-outlined text-lg">add_circle</span>
									New Trip
								</button>
							</div>

							{upcomingBookingData.length === 0 ? (
								<div className="relative overflow-hidden rounded-3xl border border-dashed border-slate-300 bg-white/50 p-20 text-center dark:border-slate-800 dark:bg-slate-900/50">
									<div className="absolute -right-20 -top-20 h-64 w-64 rounded-full bg-primary/5 blur-3xl"></div>
									<div className="absolute -left-20 -bottom-20 h-64 w-64 rounded-full bg-primary/5 blur-3xl"></div>
									<div className="relative">
										<div className="mx-auto flex h-20 w-20 items-center justify-center rounded-full bg-slate-50 dark:bg-slate-800 shadow-inner">
											<span className="material-symbols-outlined text-4xl text-slate-300">flight_takeoff</span>
										</div>
										<h3 className="mt-6 text-xl font-bold text-slate-900 dark:text-white">Adventure awaits!</h3>
										<p className="mt-2 text-slate-500 dark:text-slate-400">You don't have any upcoming trips scheduled.</p>
										<button
											onClick={() => window.location.href = '/'}
											className="mt-8 border-0 rounded-xl bg-primary px-8 py-3 font-bold text-white shadow-lg shadow-primary/25 hover:scale-105 transition-transform"
										>
											Start Planning
										</button>
									</div>
								</div>
							) : (
								<div className="grid grid-cols-1 gap-6">
									{upcomingBookingData.map((booking, index) => (
										<div key={booking.cartguidid || index} className="group relative flex flex-col overflow-hidden rounded-3xl border border-slate-200/60 bg-white/80 backdrop-blur-sm shadow-[0_8px_30px_rgb(0,0,0,0.04)] transition-all duration-500 hover:shadow-[0_20px_50px_rgb(0,0,0,0.08)] hover:-translate-y-1 dark:border-slate-800 dark:bg-slate-900/80">
											{/* Terminal images are not available — use a colored accent strip as the
											    card's visual anchor instead of a photo. */}
											<div className="h-1.5 w-full bg-gradient-to-r from-primary via-primary/80 to-amber-400"></div>

											<div className="flex flex-1 flex-col p-8">
												<div className="mb-6 flex flex-col justify-between gap-6 sm:flex-row sm:items-start">
													<div className="space-y-2">
														<div className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-primary">
															<span className="material-symbols-outlined text-base">directions_bus</span>
															Bus Express Service
														</div>
														<h3 className="text-2xl font-black tracking-tight text-slate-900 dark:text-white">
															{getDepartAndDestination(booking.details, 'Depart')} <span className="text-slate-300 mx-1">→</span> {getDepartAndDestination(booking.details, 'Destination')}
														</h3>
														<div className="flex items-center gap-2 text-sm font-medium text-slate-500">
															<div className="h-6 w-6 rounded-full bg-slate-100 flex items-center justify-center dark:bg-slate-800">
																<span className="material-symbols-outlined text-sm">confirmation_number</span>
															</div>
															Causeway Link • <span className="text-slate-700 dark:text-slate-300 font-bold">{booking.boardingcode}</span>
														</div>
													</div>
													<div className="flex flex-row items-center gap-3 sm:flex-col sm:items-end">
														<span className={`inline-flex items-center gap-1.5 rounded-full px-4 py-1.5 text-xs font-black uppercase tracking-widest ring-1 ring-inset ${booking.newcartguid
															? 'bg-amber-100 text-amber-700 ring-amber-200 dark:bg-amber-900/40 dark:text-amber-200 dark:ring-amber-900/50'
															: 'bg-primary/10 text-primary ring-primary/20 dark:bg-primary/20'
															}`}>
															<span className="h-1.5 w-1.5 rounded-full bg-current animate-pulse"></span>
															{booking.newcartguid ? 'Rescheduled' : 'Confirmed'}
														</span>
														<div className="rounded-2xl bg-slate-50 px-4 py-3 text-center dark:bg-slate-800/50 min-w-[120px] transition-colors group-hover:bg-primary/5">
															<span className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400 block mb-1">Booking Ref</span>
															<p className="text-lg font-black tracking-tighter text-primary">#{booking.boardingcode}</p>
														</div>
													</div>
												</div>

												<div className="grid grid-cols-1 gap-6 border-y border-slate-100 py-6 sm:grid-cols-3 dark:border-slate-800/50">
													<div className="flex items-center gap-4">
														<div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-slate-50 text-slate-500 transition-colors group-hover:bg-primary/10 group-hover:text-primary dark:bg-slate-800">
															<span className="material-symbols-outlined text-2xl">calendar_today</span>
														</div>
														<div>
															<p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Date</p>
															<p className="text-sm font-black text-slate-800 dark:text-slate-200">{formatDate(booking.departdate)}</p>
														</div>
													</div>
													<div className="flex items-center gap-4">
														<div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-slate-50 text-slate-500 transition-colors group-hover:bg-primary/10 group-hover:text-primary dark:bg-slate-800">
															<span className="material-symbols-outlined text-2xl">schedule</span>
														</div>
														<div>
															<p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Departure</p>
															<p className="text-sm font-black text-slate-800 dark:text-slate-200">{formatTime(booking.departtime)}</p>
														</div>
													</div>
													<div className="flex items-center gap-4">
														<div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-slate-50 text-slate-500 transition-colors group-hover:bg-primary/10 group-hover:text-primary dark:bg-slate-800">
															<span className="material-symbols-outlined text-2xl">person</span>
														</div>
														<div>
															<p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Passenger</p>
															<p className="text-sm font-black text-slate-800 dark:text-slate-200 truncate max-w-[120px]">{booking.customername || 'Guest'}</p>
														</div>
													</div>
												</div>

												<div className="mt-8 flex flex-wrap items-center justify-end gap-4">

													<a
														href="/bookingHistory?reschedule=true"
														onClick={() => linkToBookingHistory(
															booking.cartguidid,
															booking.addon,
															booking.oldcartguid,
															booking.departbookingcode,
															booking.returnbookingcode
														)}
														className="manage-trip-btn group/btn relative flex items-center gap-3 overflow-hidden rounded-full px-8 py-3.5 text-sm font-black text-white no-underline transition-all active:scale-95"
													>
														<span className="relative z-10">Manage Trip</span>
														<span className="material-symbols-outlined relative z-10 text-lg transition-transform group-hover/btn:translate-x-1">arrow_forward</span>
														<div className="absolute inset-0 z-0 bg-gradient-to-r from-white/0 via-white/10 to-white/0 transition-transform duration-1000 -translate-x-full group-hover/btn:translate-x-full"></div>
													</a>
												</div>
											</div>
										</div>
									))}
								</div>
							)}
						</div>
					) : (
						<div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-700">
							<div className="flex items-center gap-3">
								<div className="h-8 w-1 bg-slate-300 rounded-full dark:bg-slate-700"></div>
								<h2 className="text-xl font-bold text-slate-800 dark:text-slate-200">Travel History</h2>
							</div>

							<div className="overflow-hidden rounded-3xl border border-slate-200/60 bg-white/80 backdrop-blur-sm shadow-[0_8px_30px_rgb(0,0,0,0.04)] dark:border-slate-800 dark:bg-slate-900/80">
								<div className="overflow-x-auto">
									<table className="w-full text-left border-collapse">
										<thead>
											<tr className="bg-slate-50/50 border-b border-slate-100 dark:bg-slate-800/20 dark:border-slate-800">
												<th className="px-8 py-5 text-xs font-black uppercase tracking-[0.2em] text-slate-400">Date</th>
												<th className="px-8 py-5 text-xs font-black uppercase tracking-[0.2em] text-slate-400">Route & Details</th>
												<th className="px-8 py-5 text-xs font-black uppercase tracking-[0.2em] text-slate-400">Booking ID</th>
												<th className="px-8 py-5 text-xs font-black uppercase tracking-[0.2em] text-slate-400">Passenger</th>
												<th className="px-8 py-5 text-xs font-black uppercase tracking-[0.2em] text-slate-400">Status</th>
												<th className="px-8 py-5"></th>
											</tr>
										</thead>
										<tbody className="divide-y divide-slate-50 dark:divide-slate-800/50">
											{completedBookingData.length === 0 ? (
												<tr>
													<td colSpan="6" className="px-8 py-20 text-center">
														<div className="flex flex-col items-center gap-3">
															<span className="material-symbols-outlined text-5xl text-slate-200">history</span>
															<p className="text-slate-500 font-medium">No past journeys in your history.</p>
														</div>
													</td>
												</tr>
											) : (
												completedBookingData.map((booking, index) => (
													<tr key={booking.cartguidid || index} className="group hover:bg-primary/[0.02] transition-colors dark:hover:bg-primary/[0.05]">
														<td className="px-8 py-6 whitespace-nowrap text-sm font-black text-slate-700 dark:text-slate-300">
															{formatDate(booking.departdate)}
														</td>
														<td className="px-8 py-6">
															<div className="flex flex-col gap-1">
																<div className="flex items-center gap-2">
																	<span className="material-symbols-outlined text-primary text-xl">directions_bus</span>
																	<span className="text-sm font-black text-slate-900 dark:text-white">
																		{getDepartAndDestination(booking.details, 'Depart')} <span className="text-slate-300 mx-1">→</span> {getDepartAndDestination(booking.details, 'Destination')}
																	</span>
																</div>
																<span className="text-[10px] font-bold text-slate-400 uppercase ml-7 tracking-wider">Causeway Link Express</span>
															</div>
														</td>
														<td className="px-8 py-6">
															<span className="inline-flex items-center rounded-lg bg-slate-100 px-3 py-1 text-xs font-bold text-slate-600 dark:bg-slate-800 dark:text-slate-400">
																#{booking.boardingcode}
															</span>
														</td>
														<td className="px-8 py-6 text-sm font-bold text-slate-600 dark:text-slate-400">
															{booking.customername}
														</td>
														<td className="px-8 py-6">
															<span className={`inline-flex items-center gap-1.5 rounded-full px-4 py-1.5 text-[10px] font-black uppercase tracking-widest ring-1 ring-inset ${booking.newcartguid
																? 'bg-slate-50 text-slate-500 ring-slate-200 dark:bg-slate-800 dark:text-slate-400 dark:ring-slate-700'
																: 'bg-emerald-50 text-emerald-600 ring-emerald-200 dark:bg-emerald-950/30 dark:text-emerald-400 dark:ring-emerald-900/50'
																}`}>
																<span className={`h-1 w-1 rounded-full bg-current ${!booking.newcartguid && 'animate-pulse'}`}></span>
																{booking.newcartguid ? 'Rescheduled' : 'Completed'}
															</span>
														</td>
														<td className="px-8 py-6 text-right">
															<a
																href="/bookingHistory"
																title="View Details"
																onClick={() => {
																	sessionStorageSetItem('history_guid', JSON.stringify(booking.cartguidid));
																	sessionStorageSetItem('addon', JSON.stringify(booking.addon));
																}}
																className="inline-flex h-10 w-10 items-center justify-center rounded-xl text-slate-400 hover:bg-primary/10 hover:text-primary transition-all active:scale-90"
															>
																<span className="material-symbols-outlined font-bold">receipt_long</span>
															</a>
														</td>
													</tr>
												))
											)}
										</tbody>
									</table>
								</div>
							</div>
						</div>
					)}
				</main>
			</div>
		</div>
	);
}

export default ManageBooking;
